import { authenticate } from '@bear-auth/core';
import { push } from 'redux-first-history';

import config from '@/config';
import { logger } from '@/config/logger';
import { env } from '@/env';
import { getTokens, type CallbackRequestBodyBody } from '@/modules/api/codegen';
import { getStore } from '@/services/context';
import { omitUndefined } from '@/services/utilities/object';

import { useAccessDeniedStore } from '../../hooks/useAccessDeniedStore';
import { idTokenSchema } from '../../schemas/jwt';
import type { AuthUser } from '../../schemas/oauth';
import { getDemoUserWithCredentials } from '../../utils/getDemoUser';
import { bearAuthId, demoBearAuthId } from '../bear-auth/create';
import { fetchAuthUser } from '../bear-auth/fetchAuthUser';
import { getSaasCheckoutTypeCookie } from '../utils/getSaasCheckoutTypeCookie';
import { createSandboxContacts } from '../utm/createSandboxContacts';
import { getUtmParams } from '../utm/utm';
import { parseDemoRedirectUrl, parseRedirectUrl } from './parseRedirectUrl';

export async function signInWithOAuthResult(search: string) {
    const result = parseRedirectUrl(search);

    const body = omitUndefined<CallbackRequestBodyBody>({
        utmParams: getUtmParams(),
        saasCheckoutType: env.VITE_SAAS ? getSaasCheckoutTypeCookie() : undefined,
    });

    const { data: tokens } = await getTokens({ code: result.code }, body, {
        headers: {
            'x-islocalhost': config.isLocalhost ? 'true' : 'false',
        },
    });

    let authInfo: AuthUser;

    try {
        authInfo = await fetchAuthUser(tokens.access_token!);
    } catch (error) {
        if (useAccessDeniedStore.getState().isDenied && tokens.id_token) {
            const { jwtDecode } = await import('jwt-decode');
            const decoded = idTokenSchema.parse(jwtDecode(tokens.id_token));

            logger.debug(
                'Fetching auth user failed because the user access is denied.',
                'Storing access token and name in access denied store. Decoded ID token name: ',
                decoded.name,
            );

            // The access token is required to request access on RequestAccessPage.
            // We need to store it in the access denied store because the user won't be authenticated and therefore
            // the access token won't be available in the BearAuth session.
            useAccessDeniedStore.getState().setAccessDenied({
                name: decoded.name,
                accessToken: tokens.access_token,
            });

            return;
        }

        throw error;
    }

    await authenticate<AuthUser>(bearAuthId, {
        accessToken: tokens.access_token!,
        refreshToken: tokens.refresh_token!,
        expiration: new Date(tokens.expiry_date!).toISOString(),
        authInfo: {
            ...authInfo,
            isFirstLoginSaas: tokens.isFirstLoginSaas ?? false,
        },
    });

    const { dispatch } = await getStore();
    // Restore the URL state or redirect to home
    dispatch(push(result.state && 'url' in result.state ? result.state.url : config.routes.home));
}

export async function demoSignInWithOAuthResult(hash: string) {
    const { dispatch } = await getStore();
    dispatch(push(config.routes.home));

    const result = parseDemoRedirectUrl(hash);

    const { jwtDecode } = await import('jwt-decode');

    const decodedIdToken = idTokenSchema.parse(jwtDecode(result.id_token));

    const userWithCredentials = getDemoUserWithCredentials();

    await authenticate<AuthUser>(demoBearAuthId, {
        accessToken: userWithCredentials.accessToken,
        expiration: userWithCredentials.expiration,
        authInfo: {
            ...userWithCredentials.authInfo,
            decodedIdToken,
            idToken: result.id_token,
        },
    });

    await createSandboxContacts(decodedIdToken.email);
}
